import { gameLogger } from '../../../utils/GameLogger';
import { TextureGenerator } from '../../utils/rendering/TextureGenerator';
import { AppearanceHash } from '../../../shared/utils/AppearanceHash';
import type { GhostAppearance } from '../../../shared/types/AppearanceTypes';
import type { IAssets } from '../../assets/IAssets';

// Phase H10 Arc λ.10 (2026-05-10): IAssets stub for callers that pass
// an unknown / Phaser.Scene to getInstance(). Texture reads/writes no-op
// silently (was the PhaserAssets path; PhaserAssets deleted in this batch).
const noopAssets: IAssets = {
  hasTexture: () => false,
  getTexture: () => null,
  loadImage: () => Promise.resolve(),
  loadBatch: () => Promise.resolve(),
  addTextureFromCanvas: () => undefined,
  removeTexture: () => undefined,
  onProgress: () => undefined,
} as unknown as IAssets;

// Phase H10 Arc λ.9 Stage B4 phase 2 — SpriteTextureSystem accepts an
// IAssets directly. Caller picks the impl. The legacy scene-only ctor
// path stays available for callers that haven't migrated; it constructs
// a PhaserAssets internally during the transition.

/**
 * Texture loading states
 */
export enum TextureLoadState {
  NOT_LOADED = 'not_loaded',
  LOADING = 'loading',
  LOADED = 'loaded',
  FAILED = 'failed'
}

/**
 * Texture request configuration
 */
export interface TextureRequest {
  key?: string; // Optional: If not provided, will be generated from appearance hash
  type: 'player' | 'mob' | 'npc' | 'item' | 'prop' | 'effect';
  dataUrl?: string;
  appearance?: any; // GhostProps
  fallbackKey?: string;
  priority?: number;
}

/**
 * Texture entry in the cache
 */
interface TextureEntry {
  key: string;
  state: TextureLoadState;
  timestamp: number;
  refCount: number; // Number of active users of this texture
  request: TextureRequest;
  error?: Error;
  callbacks: Array<(success: boolean, key: string) => void>;
}

/**
 * SpriteTextureSystem
 *
 * Centralized management for all texture generation and loading in Helix 2000.
 * Implements content-addressable storage (hashing) for Ghost textures to strictly
 * eliminate duplication.
 *
 * Key Features:
 * - Hash-based deduplication (same appearance = same texture)
 * - Reference counting (only cleanup unused textures)
 * - Async loading queue
 *
 * Usage:
 *   const textureSystem = SpriteTextureSystem.getInstance(scene);
 *   const textureKey = await textureSystem.loadTexture({
 *     type: 'player',
 *     appearance: player.appearance
 *   });
 *   sprite.setTexture(textureKey);
 */
export class SpriteTextureSystem {
  private static instance: SpriteTextureSystem | null = null;
  // Phase H10 Arc λ.9 Stage B4 phase 2 — scene field retired. State that
  // depended on it now reads through `this.assets` (IAssets surface).
  private assets: IAssets | null = null;
  private textureCache = new Map<string, TextureEntry>();
  private cacheHits = 0;
  private cacheMisses = 0;

  // Default fallback textures that must always exist
  private static readonly FALLBACK_TEXTURES = {
    PLAYER: 'fallback_player',
    MOB: 'fallback_mob',
    NPC: 'fallback_npc',
    ITEM: 'fallback_item',
    PROP: 'fallback_prop'
  };

  private constructor(assets: IAssets) {
    this.assets = assets;
    this.initializeFallbackTextures();
  }

  /**
   * Get the singleton instance.
   *
   * Phase H10 Arc λ.10 (2026-05-10): PhaserAssets fallback dropped — the
   * sceneOrAssets argument MUST be an IAssets impl (PixiAssets in production
   * post-λ.7). Legacy Phaser.Scene callers either migrate or fall through
   * to a stub IAssets that no-ops texture reads. PhaserAssets adapter file
   * deleted in this batch.
   *
   * Singleton identity is keyed on the INPUT reference. Same input → same
   * instance. Different input → fresh instance.
   */
  private static instanceKey: IAssets | null = null;
  public static getInstance(sceneOrAssets: IAssets | unknown): SpriteTextureSystem {
    const candidate = sceneOrAssets as IAssets;
    const isAssets = typeof candidate?.hasTexture === 'function';
    const assets: IAssets = isAssets ? candidate : noopAssets;

    if (SpriteTextureSystem.instance && SpriteTextureSystem.instanceKey === assets) {
      return SpriteTextureSystem.instance;
    }

    SpriteTextureSystem.instance = new SpriteTextureSystem(assets);
    SpriteTextureSystem.instanceKey = assets;
    return SpriteTextureSystem.instance;
  }

  /**
   * Reset the singleton (useful for scene restarts)
   */
  public static reset(): void {
    SpriteTextureSystem.instance = null;
    SpriteTextureSystem.instanceKey = null;
  }

  /**
   * Generate a deterministic texture key for a mob
   */
  public static generateMobKey(mobId: string, mobType: string): string {
    return `mob_${mobType}_${mobId}`;
  }

  /**
   * Generate a deterministic texture key for an NPC
   */
  public static generateNPCKey(npcId: string, npcType: string): string {
    return `npc_${npcType}_${npcId}`;
  }

  /**
   * Get a texture by key and type. Convenience wrapper that loads if needed.
   * Returns the final texture key (which may be a fallback).
   */
  public async getTexture(key: string, type: TextureRequest['type']): Promise<string> {
    if (this.isTextureReady(key)) {
      return key;
    }
    return this.loadTexture({ key, type });
  }

  /**
   * Initialize fallback textures that are used when primary textures fail to load
   */
  private initializeFallbackTextures(): void {
    // Phase H10 Arc λ.9 Stage B4 phase 2 — `if (!this.scene) return` retired;
    // canvas-2D fallback gen runs in any browser-side env (gated by
    // `document.createElement` try/catch inside makeFallbackDataUri).

    // Create a simple colored square for each fallback type
    const fallbacks = [
      { key: SpriteTextureSystem.FALLBACK_TEXTURES.PLAYER, color: 0x4a90d9 },
      { key: SpriteTextureSystem.FALLBACK_TEXTURES.MOB, color: 0xe74c3c },
      { key: SpriteTextureSystem.FALLBACK_TEXTURES.NPC, color: 0xf39c12 },
      { key: SpriteTextureSystem.FALLBACK_TEXTURES.ITEM, color: 0x2ecc71 },
      { key: SpriteTextureSystem.FALLBACK_TEXTURES.PROP, color: 0x95a5a6 }
    ];

    for (const { key, color } of fallbacks) {
      if (this.assets?.hasTexture(key)) continue;

      // Phase H10 Arc λ.9 Stage B1 — fallback texture generation moved off
      // `scene.add.graphics().generateTexture()` to substrate-agnostic
      // canvas drawing. Same shape (circle + 2 eye slits) but produced
      // as a data URI and registered through IAssets.loadImage. Decouples
      // SpriteTextureSystem from Phaser's scene plugin.
      const dataUri = SpriteTextureSystem.makeFallbackDataUri(color);
      // Fire-and-forget — IAssets.loadImage is async but the texture
      // appears in the cache once decoded. textureCache entry is set
      // immediately so consumers waiting on hasTexture() are unblocked
      // by the IAssets-side callback.
      void this.assets?.loadImage(key, dataUri);

      this.textureCache.set(key, {
        key,
        state: TextureLoadState.LOADED,
        timestamp: Date.now(),
        refCount: 1,
        request: { key, type: 'prop' },
        callbacks: [],
      });
    }
  }

  /** Substrate-agnostic fallback texture: 64×64 colored circle with
   *  white outline and 2 horizontal eye slits. Same visual as the
   *  prior Phaser-graphics version, but generated as an SVG data URI to
   *  avoid the synchronous PNG encode in `canvas.toDataURL()` that caused
   *  ~170–280 ms main-thread stalls during boot. */
  private static makeFallbackDataUri(color: number): string {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const bodyFill = `rgba(${r},${g},${b},0.8)`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">`
      + `<circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="2"/>`
      + `<circle cx="32" cy="32" r="26" fill="${bodyFill}"/>`
      + `<rect x="22" y="28" width="6" height="2" fill="rgba(255,255,255,0.9)"/>`
      + `<rect x="36" y="28" width="6" height="2" fill="rgba(255,255,255,0.9)"/>`
      + `</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }

  public static generateAppearanceKey(appearance: GhostAppearance | null | undefined): string {
    return appearance ? `ghost_${AppearanceHash.generate(appearance)}` : 'ghost_default';
  }

  /**
   * Check if a texture exists and is loaded
   */
  public isTextureReady(key: string): boolean {
    if (!this.assets) return false;

    const entry = this.textureCache.get(key);
    if (entry) {
      return entry.state === TextureLoadState.LOADED;
    }

    // Phase H10 Arc η — texture-existence check via IAssets surface.
    return this.assets.hasTexture(key);
  }

  /**
   * Load a texture asynchronously. Returns a promise that resolves when the texture is ready.
   * Automatically handles deduplication via hashing.
   */
  public async loadTexture(request: TextureRequest): Promise<string> {
    if (!this.assets) {
      throw new Error('SpriteTextureSystem: IAssets not initialized');
    }

    let { key, type, dataUrl, appearance } = request;

    // 1. Determine the effective Key
    if (!key && appearance) {
        key = SpriteTextureSystem.generateAppearanceKey(appearance as GhostAppearance);
    } else if (!key) {
        // Fallback for non-appearance items
        gameLogger.warn('sprite', 'LoadTexture called without Key or Appearance');
        return this.getFallbackKey(type);
    }

    // 2. Check Cache
    const existingEntry = this.textureCache.get(key!);

    // If fully loaded, just return
    if (existingEntry && existingEntry.state === TextureLoadState.LOADED) {
      existingEntry.timestamp = Date.now(); // Update LRU timestamp
      existingEntry.refCount++; // Increment usage
      this.cacheHits++;
      return key!;
    }

    // If currently loading, join the queue
    if (existingEntry && existingEntry.state === TextureLoadState.LOADING) {
      gameLogger.debug('sprite', `Waiting for texture dedupe: ${key}`);
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Texture load timeout: ${key}`));
        }, 10000);

        existingEntry.callbacks.push((success, loadedKey) => {
          clearTimeout(timeout);
          if (success) {
            existingEntry.refCount++;
            resolve(loadedKey);
          } else {
            reject(new Error(`Failed to load texture: ${key}`));
          }
        });
      });
    }

    // 3. Prepare Data
    let finalDataUrl = dataUrl;
    if (!finalDataUrl && appearance) {
      finalDataUrl = TextureGenerator.generateGhostSVG(appearance);
    }

    if (!finalDataUrl) {
      gameLogger.warn('sprite', `No data URL or appearance for texture: ${key}, using fallback`);
      return this.getFallbackKey(type);
    }

    this.cacheMisses++;

    // 4. Create Entry
    const entry: TextureEntry = {
      key: key!,
      state: TextureLoadState.LOADING,
      timestamp: Date.now(),
      refCount: 1,
      request,
      callbacks: []
    };
    this.textureCache.set(key!, entry);

    // 5. Load
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        entry.state = TextureLoadState.FAILED;
        entry.error = new Error('Load timeout');
        this.notifyCallbacks(entry, false);
        reject(new Error(`Texture load timeout: ${key}`));
      }, 10000);

      this.loadTextureAsync(key!, finalDataUrl!)
        .then(() => {
          clearTimeout(timeout);
          entry.state = TextureLoadState.LOADED;
          this.notifyCallbacks(entry, true);
          resolve(key!);
        })
        .catch((error) => {
          clearTimeout(timeout);
          entry.state = TextureLoadState.FAILED;
          entry.error = error;
          this.notifyCallbacks(entry, false);

          gameLogger.error('sprite', `Failed to load texture: ${key}`, error);
          // Resolve with fallback instead of rejecting
          resolve(this.getFallbackKey(type));
        });
    });
  }

  /**
   * Release a reference to a texture.
   * Call this when a sprite is destroyed.
   */
  public releaseTexture(key: string): void {
      const entry = this.textureCache.get(key);
      if (entry) {
          entry.refCount--;
          if (entry.refCount < 0) entry.refCount = 0;
      }
  }

  /**
   * Clean up unused textures (refCount <= 0) that are old
   */
  public cleanup(maxAgeMs: number = 5 * 60 * 1000): void {
    const now = Date.now();
    const toDelete: string[] = [];

    this.textureCache.forEach((entry, key) => {
      // Don't delete fallback textures
      if (Object.values(SpriteTextureSystem.FALLBACK_TEXTURES).includes(key)) {
        return;
      }

      // Cleanup criteria: No refs AND old enough
      if (entry.refCount <= 0 && (now - entry.timestamp > maxAgeMs)) {
        toDelete.push(key);
      }
    });

    for (const key of toDelete) {
      this.textureCache.delete(key);
      // Phase H10 Arc λ.9 Stage B4 phase 2 — eviction routes through IAssets
      // instead of `scene.textures.remove`. PhaserAssets delegates to the
      // Phaser texture cache; PixiAssets clears its own Map + Pixi Assets.unload.
      this.assets?.removeTexture(key);
    }

    if (toDelete.length > 0) {
      gameLogger.info('sprite', `Cleaned up ${toDelete.length} unused textures`);
    }
  }

  /**
   * Dispose of the system and clean up resources
   */
  public dispose(): void {
    this.textureCache.clear();
    SpriteTextureSystem.instance = null;
  }

  public getStats(): { total: number; loaded: number; used: number } {
      let used = 0;
      this.textureCache.forEach(e => { if(e.refCount > 0) used++; });
      return {
          total: this.textureCache.size,
          loaded: this.textureCache.size, // Approximation
          used
      };
  }

  /**
   * Get detailed cache statistics including hit/miss rates.
   */
  public getCacheStats(): { total: number; loaded: number; used: number; hits: number; misses: number; hitRate: number } {
      const basic = this.getStats();
      const totalRequests = this.cacheHits + this.cacheMisses;
      return {
          ...basic,
          hits: this.cacheHits,
          misses: this.cacheMisses,
          hitRate: totalRequests > 0 ? this.cacheHits / totalRequests : 0,
      };
  }

  /**
   * Load a character texture by appearance.
   * Convenience wrapper around loadTexture that generates the hash-based key.
   */
  public async loadCharacterTexture(appearance: GhostAppearance): Promise<string> {
    const key = SpriteTextureSystem.generateAppearanceKey(appearance);
    return this.loadTexture({
      key,
      type: 'player',
      appearance,
    });
  }

  /**
   * Release a character texture by appearance.
   * Convenience wrapper around releaseTexture using the appearance hash.
   */
  public releaseCharacterTexture(appearance: GhostAppearance): void {
    const key = SpriteTextureSystem.generateAppearanceKey(appearance);
    this.releaseTexture(key);
  }

  // Private helper methods

  private async loadTextureAsync(key: string, dataUrl: string): Promise<void> {
    if (!this.assets) throw new Error('IAssets not available');
    // Phase H10 Arc η — routes through IAssets.loadImage. The Phaser-side
    // PhaserAssets impl uses the same Image.onload + scene.textures.addImage
    // pattern this method used inline; the Pixi-side PixiAssets impl
    // (future) will route to Pixi's Assets API.
    return this.assets.loadImage(key, dataUrl);
  }

  private notifyCallbacks(entry: TextureEntry, success: boolean): void {
    for (const callback of entry.callbacks) {
      try {
        callback(success, entry.key);
      } catch (error) {
        gameLogger.error('sprite', `Error in texture callback for ${entry.key}`, error);
      }
    }
    entry.callbacks = [];
  }

  public getFallbackKey(type: TextureRequest['type']): string {
    switch (type) {
    case 'player': return SpriteTextureSystem.FALLBACK_TEXTURES.PLAYER;
    case 'mob': return SpriteTextureSystem.FALLBACK_TEXTURES.MOB;
    case 'npc': return SpriteTextureSystem.FALLBACK_TEXTURES.NPC;
    case 'item': return SpriteTextureSystem.FALLBACK_TEXTURES.ITEM;
    case 'prop':
    default: return SpriteTextureSystem.FALLBACK_TEXTURES.PROP;
    }
  }
}

// Export singleton access for convenience
export const getSpriteTextureSystem = (scene: unknown): SpriteTextureSystem => {
  return SpriteTextureSystem.getInstance(scene);
};
